import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import CockpitModal from './CockpitModal';
import { compareValues, matchesQuery } from '../../utils/cockpitsListView.js';
import { useLocation, useNavigate } from 'react-router-dom';



const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, itemName }) => {
  const [confirmText, setConfirmText] = useState('');
  const isConfirmEnabled = confirmText === 'DELETE';

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isOpen ? '' : 'hidden'}`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Confirmer la suppression
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Cette action est irréversible. Pour confirmer la suppression de <strong>{itemName}</strong>, veuillez écrire <strong>DELETE</strong> ci-dessous.
        </p>

        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Tapez DELETE"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white mb-4"
        />

        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={!isConfirmEnabled}
            className={`px-4 py-2 rounded-md transition-colors ${
              isConfirmEnabled
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-red-300 text-white cursor-not-allowed dark:bg-red-800'
            }`}
          >
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
};

const RestoreConfirmationModal = ({ isOpen, onClose, onConfirm, itemName }) => {
  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isOpen ? '' : 'hidden'}`}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Confirmer la restauration
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Êtes-vous sûr de vouloir restaurer le rapport <strong>{itemName}</strong> ?
        </p>

        <div className="flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Restaurer
          </button>
        </div>
      </div>
    </div>
  );
};


const CockpitsManagement = ({ fetchWithAuth, currentUser }) => {
  const [cockpits, setCockpits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentCockpit, setCurrentCockpit] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cockpitToDelete, setCockpitToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [cockpitToRestore, setCockpitToRestore] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  // Get query parameters and navigation
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const editCockpitId = queryParams.get('edit');

  const fetchCockpits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/cockpits${showDeleted ? '?deleted=true' : ''}`);
      if (response.ok) {
        const data = await response.json();
        setCockpits(data);

        // If edit parameter is present, find and open the cockpit for editing
        if (editCockpitId) {
          const cockpitToEdit = data.find(cockpit => cockpit.id.toString() === editCockpitId);
          if (cockpitToEdit) {
            setCurrentCockpit(cockpitToEdit);
            setIsModalOpen(true);
          }
        }
      } else {
        throw new Error('Failed to fetch cockpits');
      }
    } catch (err) {
      console.error('Error fetching cockpits:', err);
      setError('Impossible de charger les cockpits. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, showDeleted, editCockpitId]);

  useEffect(() => {
    fetchCockpits();
  }, [fetchCockpits]);

  const handleDeleteClick = (cockpit) => {
    setCockpitToDelete(cockpit);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cockpitToDelete) return;

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/cockpits/${cockpitToDelete.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setCockpits(cockpits.filter(cockpit => cockpit.id !== cockpitToDelete.id));
        setDeleteModalOpen(false);
        setCockpitToDelete(null);
      } else {
        throw new Error('Failed to delete cockpit');
      }
    } catch (error) {
      console.error('Error deleting cockpit:', error);
      alert('Erreur lors de la suppression du cockpit');
    }
  };

  const handleRestoreClick = (cockpit) => {
    setCockpitToRestore(cockpit);
    setRestoreModalOpen(true);
  };

  const handleRestoreConfirm = async () => {
    if (!cockpitToRestore) return;

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/admin/cockpits/${cockpitToRestore.id}/restore`, {
        method: 'POST'
      });

      if (response.ok) {
        await fetchCockpits();
        setRestoreModalOpen(false);
        setCockpitToRestore(null);
      } else {
        throw new Error('Failed to restore cockpit');
      }
    } catch (error) {
      console.error('Error restoring cockpit:', error);
      alert('Erreur lors de la restauration du cockpit');
    }
  };

  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filtrer AVANT le court-circuit du tri : sinon la recherche resterait sans
  // effet tant qu'aucune colonne n'est triée.
  const getFilteredAndSortedData = () => {
    const filtered = cockpits.filter((cockpit) => matchesQuery(cockpit, searchQuery));
    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a, b) => {
      if (sortConfig.key === 'tabs') {
        const aTabs = a.config?.tabs?.length || 0;
        const bTabs = b.config?.tabs?.length || 0;
        return sortConfig.direction === 'asc' ? aTabs - bTabs : bTabs - aTabs;
      }

      if (sortConfig.key === 'is_public') {
        const aPublic = a.is_public ? 1 : 0;
        const bPublic = b.is_public ? 1 : 0;
        return sortConfig.direction === 'asc' ? aPublic - bPublic : bPublic - aPublic;
      }

      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      return sortConfig.direction === 'asc'
        ? compareValues(aValue, bValue)
        : compareValues(bValue, aValue);
    });
  };

  const SortIndicator = ({ column }) => {
    if (sortConfig.key !== column) {
      return (
        <svg className="w-4 h-4 ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 ml-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 ml-1 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Handle modal close and clear URL parameter
  const handleModalClose = () => {
    setIsModalOpen(false);
    // Clear the edit parameter from URL if it exists
    if (editCockpitId) {
      navigate('/admin/cockpits', { replace: true });
    }
  };

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Erreur!</strong>
        <span className="block sm:inline"> {error}</span>
      </div>
    );
  }

  const rows = getFilteredAndSortedData();

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100"> 
              Configurations de cockpits   
          </h2>
          {currentUser?.issuperadmin && (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showDeleted"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label htmlFor="showDeleted" className="text-sm text-gray-600 dark:text-gray-300">
                Afficher les cockpits archivés
              </label>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <label htmlFor="cockpitSearch" className="sr-only">Rechercher un cockpit</label>
            <input
              id="cockpitSearch"
              ref={searchInputRef}
              type="text"
              placeholder="Rechercher un cockpit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
                aria-label="Effacer la recherche"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {currentUser?.issuperadmin && (
            <button
              onClick={() => {
                setCurrentCockpit(null);
                setIsModalOpen(true);
              }}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors whitespace-nowrap"
            >
              Ajouter un cockpit
            </button>
          )}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setCockpitToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={cockpitToDelete?.name}
      />

      <RestoreConfirmationModal
        isOpen={restoreModalOpen}
        onClose={() => {
          setRestoreModalOpen(false);
          setCockpitToRestore(null);
        }}
        onConfirm={handleRestoreConfirm}
        itemName={cockpitToRestore?.name}
      />
      {loading ? (
        // Le champ de recherche reste monté au-dessus : un rechargement (bascule
        // « archivés ») ne doit ni vider la saisie ni voler le focus.
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4">
          <p className="text-yellow-700 dark:text-yellow-300">
            {searchQuery
              ? 'Aucun cockpit ne correspond à cette recherche.'
              : 'Aucun cockpit trouvé.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th
                  onClick={() => sortData('id')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                >
                  <div className="flex items-center">
                    ID
                    <SortIndicator column="id" />
                  </div>
                </th>
                <th
                  onClick={() => sortData('name')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                >
                  <div className="flex items-center">
                    Nom
                    <SortIndicator column="name" />
                  </div>
                </th>
                <th
                  onClick={() => sortData('description')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                >
                  <div className="flex items-center">
                    Description
                    <SortIndicator column="description" />
                  </div>
                </th>
                <th
                  onClick={() => sortData('tabs')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                >
                  <div className="flex items-center">
                    Onglets
                    <SortIndicator column="tabs" />
                  </div>
                </th>
                <th
                  onClick={() => sortData('is_public')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer"
                >
                  <div className="flex items-center">
                    Visibilité
                    <SortIndicator column="is_public" />
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Ouvrir
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((cockpit) => (
                <tr
                  key={cockpit.id}
                  className={`
                    hover:bg-gray-50 dark:hover:bg-gray-700
                    ${cockpit.deleted_at
                      ? 'bg-gray-100 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 line-through'
                      : 'bg-white dark:bg-gray-800'
                    }
                  `}
                >
                  <td className="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{cockpit.id}</td>
                  <td className="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{cockpit.name}</td>
                  <td className="px-6 py-4 text-left text-sm text-gray-900 dark:text-gray-200">{cockpit.description || 'Aucune description'}</td>
                  <td className="px-6 py-4 text-left whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{cockpit.config && cockpit.config.tabs ? cockpit.config.tabs.length : 0}</td>
                  <td className="px-6 py-4 text-left whitespace-nowrap text-sm">
                    {cockpit.is_public ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">
                        Public
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                        Privé
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                    <button
                      onClick={() => window.open(`/c/${cockpit.slug}`, '_blank')}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      title="Ouvrir le cockpit"
                      disabled={cockpit.deleted_at}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                    <div className="flex items-center justify-center space-x-3">
                      <button
                        onClick={() => {
                          setCurrentCockpit(cockpit);
                          setIsModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Modifier le cockpit"
                        disabled={cockpit.deleted_at}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      {currentUser?.issuperadmin && (
                        <>
                          {cockpit.deleted_at ? (
                            <button
                              onClick={() => handleRestoreClick(cockpit)}
                              className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              title="Restaurer le cockpit"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeleteClick(cockpit)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              title="Supprimer le cockpit"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CockpitModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        cockpit={currentCockpit}
        fetchWithAuth={fetchWithAuth}
        onSave={() => {
          handleModalClose();
          fetchCockpits();
        }}
      />
    </div>
  );
};

export default CockpitsManagement;